import React, { useState } from 'react'

interface SmartImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  image: string // Image hash only
  size: 'small' | 'medium' | 'large'
  alt: string
  isTemp?: boolean // true for temp patterns
  previewUrl?: string // Local preview URL for immediate display
  onCloudFrontLoad?: () => void // Called when CloudFront image loads successfully
  fallback?: string // Fallback text/emoji when image fails
}

export function SmartImage({
  image,
  size,
  alt,
  onError,
  isTemp = false,
  previewUrl,
  onCloudFrontLoad,
  ...props
}: SmartImageProps) {
  const [failed, setFailed] = useState(false)
  const [usePreview, setUsePreview] = useState(!!previewUrl)
  const [triedFallback, setTriedFallback] = useState(false)
  const baseUrl = import.meta.env.REACT_APP_CLOUDFRONT_URL || 'https://d29u3sxm1v0m5f.cloudfront.net'

  const cloudFrontSrc = isTemp
    ? `${baseUrl}/uploads/temp/${image}-${size}.webp`
    : `${baseUrl}/processed/${image}-${size}.webp`

  // Use preview URL if available and CloudFront hasn't loaded yet
  const imageSrc = usePreview && previewUrl ? previewUrl : cloudFrontSrc

  const handleLoad = () => {
    // If we successfully loaded from CloudFront, stop using preview
    if (!usePreview || !previewUrl) {
      onCloudFrontLoad?.()
      return
    }

    // We loaded the preview, now try CloudFront in background
    const testImg = new Image()
    testImg.onload = () => {
      setUsePreview(false)
      onCloudFrontLoad?.()
    }
    testImg.onerror = () => {
      // CloudFront not ready yet, keep using preview
      setTimeout(() => {
        testImg.src = cloudFrontSrc // Retry in 2 seconds
      }, 2000)
    }
    testImg.src = cloudFrontSrc
  }

  const handleError = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget
    const currentSrc = img.src

    // Bidirectional fallback: temp ↔ processed
    if (!triedFallback) {
      setTriedFallback(true)
      if (currentSrc.includes('/uploads/temp/')) {
        // Temp failed, try processed
        img.src = `${baseUrl}/processed/${image}-${size}.webp`
        return
      } else if (currentSrc.includes('/processed/')) {
        // Processed failed, try temp
        img.src = `${baseUrl}/uploads/temp/${image}-${size}.webp`
        return
      }
    }

    setFailed(true)
    onError?.(e)
  }

  if (failed) {
    return (
      <div
        {...props}
        style={{
          ...props.style,
          backgroundColor: '#f5f5f5',
          border: '1px solid #e0e0e0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px',
        }}
      >
        Image not found
      </div>
    )
  }

  return (
    <img
      src={imageSrc}
      alt={alt}
      onLoad={handleLoad}
      onError={handleError}
      loading="lazy"
      crossOrigin="anonymous"
      {...props}
    />
  )
}
